<template>
  <v-sheet class="device-list" color="transparent">
    <template v-if="props.microphone">
      <div class="group" v-if="showMicTitle">
        <v-icon icon="msf:mic" />
        {{ $t(`${screenKey}.devices.microphone`) }}
      </div>
      <v-list
        bg-color="transparent"
        density="compact"
        :selected="[selectedMic]"
        :mandatory="true"
        :slim="true"
        @click:select="handleMicChange"
      >
        <v-list-item
          class="device-item"
          v-for="device in microphones"
          :key="device.deviceId"
          :value="device.deviceId"
          :active="device.deviceId === selectedMic"
          bg-color="transparent"
          base-color="default"
          color="primary"
          variant="text"
        >
          <template v-slot:append>
            <v-icon v-if="device.deviceId === selectedMic" icon="msf:check" color="primary"></v-icon>
          </template>
          <v-list-item-title v-text="device.label"></v-list-item-title>
        </v-list-item>
      </v-list>
      <v-divider class="pb-4" v-if="showSpeaker" />
    </template>
    <template v-if="showSpeaker">
      <div class="group" v-if="props.microphone">
        <v-icon icon="msf:volume_up" />
        {{ $t(`${screenKey}.devices.speaker`) }}
      </div>
      <v-list
        bg-color="transparent"
        density="compact"
        :selected="[selectedSpeaker]"
        :mandatory="true"
        :slim="true"
        :disabled="!isSpeakerSupported"
        @click:selected="handleSpeakerChange"
      >
        <v-list-item
          class="device-item"
          v-for="device in speakers"
          :key="device.deviceId"
          :value="device.deviceId"
          :active="device.deviceId === selectedSpeaker"
          :disabled="!isSpeakerSupported"
          bg-color="transparent"
          base-color="default"
          color="primary"
          variant="text"
        >
          <template v-slot:append>
            <v-icon v-if="device.deviceId === selectedSpeaker" icon="msf:check" color="primary"></v-icon>
          </template>
          <v-list-item-title v-text="device.label"></v-list-item-title>
        </v-list-item>
      </v-list>
    </template>
  </v-sheet>
</template>
<script setup>
import {useDevicesList} from '@vueuse/core'
import {computed} from 'vue'
import {useSpeaker} from '@/composables'
import {useStreamStore} from '@/stores/streamStore'
import {storeToRefs} from 'pinia'
import {useAppStore} from '@/stores'
import {useDisplay} from 'vuetify'

const props = defineProps({
  microphone: {tyep: Boolean, default: true},
  speaker: {type: Boolean, default: true},
})

// Shared by the pre-call and meeting screens, each screen has its own texts
const app = useAppStore()
const screenKey = computed(() => (app.isPreCall ? 'preCall' : 'meeting'))

const {sinkId, isSupported: isSpeakerSupported, change: changeSpeaker} = useSpeaker()
const {audioOutputs} = useDevicesList()
const streamStore = useStreamStore()
const {micStream, selectedMicId, micEnabled, microphones} = storeToRefs(streamStore)
const selectedMic = computed(() => micStream.value?.getAudioTracks()[0]?.getSettings().deviceId)
const speakers = computed(() =>
  audioOutputs.value.filter(device => device.deviceId !== 'default' && device.deviceId !== 'communications')
)
// Hide section when no speaker is listed (e.g. mobile, permission not granted)
// setSinkId is not supported on mobile devices, hide the section there
const {platform} = useDisplay()
const isMobileDevice = computed(() => platform.value.android || platform.value.ios)
const showSpeaker = computed(() => props.speaker && !isMobileDevice.value)
const showMicTitle = props.speaker
// Saved sink may be 'default' (filtered out of the list): fall back to the first real device
const selectedSpeaker = computed(() => {
  const ids = speakers.value.map(device => device.deviceId)
  return ids.includes(sinkId.value) ? sinkId.value : ids[0]
})
const handleSpeakerChange = async event => {
  const deviceId = event.id
  const deviceLabel = speakers.value.find(d => d.deviceId === deviceId).label
  if (!isSpeakerSupported || deviceId === selectedSpeaker.value) return
  try {
    await changeSpeaker(deviceId, deviceLabel)
  } catch (error) {
    console.error(`[handleSpeakerChange] Switching speaker failed:`, error)
  }
}
const handleMicChange = async event => {
  selectedMicId.value = event.id
  if (!micEnabled.value) {
    micEnabled.value = true
  }
  // TODO handle mic change in strategy as well
}
</script>
<style lang="scss" scoped>
.device-list {
  padding: $spacing-s $spacing-m 0 $spacing-m;
}
.group {
  display: flex;
  align-items: center;
  flex-direction: row;
  height: 32px;
  padding: $spacing-xs $spacing-s;
  gap: $spacing-xxs;
  font-weight: bold;
}
.device-item {
  :deep(.v-list-item__overlay) {
    background: transparent;
  }
}
</style>
